import Abstract3dModule from './Abstract3dModule';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { clusterLocations, getApproxLongitudeFromUser, vector3ToLatLong } from '../utils/earth';
import * as THREE from 'three';
import gsap from 'gsap';
import { textureLoader } from '../utils/3d';
import Earth from '../classes/explore/Earth';
import Label from '../classes/explore/Label';
import ExploreSceneTweakpane from '../utils/mixins/ExploreSceneTweakpane';
import SunHandler from '../utils/mixins/SunHandler';
import CamerasHandler, { CAMERA_MODE } from '../utils/mixins/CamerasHandler';

export default class extends Abstract3dModule {
    constructor(m) {
        super(m);

        this.DEBUG = true;

        this.defaultCameraMode = CAMERA_MODE.SPLASH;  

        this.canvas = this.$('canvas')[0]
        this.$labels = this.$('labels')[0]        
        
        this.orbitDomElement = this.$labels;        

        this.events = {
            click: {
                setLocationsFromEl: 'setLocationsFromEl',
                goToCoordinatesFromEl: 'goToCoordinatesFromEl'
            }
        }

        Object.assign(this, SunHandler);
        Object.assign(this, CamerasHandler);
    }

    init() {
        // Delay init to prevent jittering on page enter
        setTimeout(() => {
            super.init();                     
    
            if(this.DEBUG) {
                // Mixin tweakpane methods
                Object.assign(this, ExploreSceneTweakpane)
                this.initTweakPane();
            }
        }, 1000)
    }     

    initRenderer() {
        super.initRenderer()

        
        this.labelRenderer = new CSS2DRenderer({ element: this.$labels });
        this.labelRenderer.setSize( window.innerWidth, window.innerHeight );
        this.labelRenderer.domElement.classList.add('c-explore_labels')        
        // this.el.parentNode.appendChild( this.labelRenderer.domElement );
    }

    initScene() {
        super.initScene();
            
        this.initCameras();
        requestAnimationFrame(() => {
            this.goToEstimatedUserLocation();
        })

        const map = textureLoader.load('/themes/populous/static/assets/images/milkyway.webp', () => {
            gsap.to(this.envMaterial, { opacity: 1, duration: 4 });
        });
        map.colorSpace = THREE.SRGBColorSpace;
        map.anisotropy = this.renderer.capabilities.getMaxAnisotropy();        

        this.envGeometry = new THREE.SphereGeometry( 8, 128, 64 ); 
        this.envMaterial = new THREE.MeshBasicMaterial({
            map,
            side: THREE.BackSide,
            color: 0x707070,
            transparent: true,
            opacity: 0
        });

        this.env = new THREE.Mesh( this.envGeometry, this.envMaterial );
        this.wrapper.add(this.env)
        
        this.earth = new Earth(this.renderer);
        this.wrapper.add(this.earth.object); 

        this.addSun();    
    }

    async setLabels(slug) {
        if(this.labelsSlug == slug) {
            console.warn('Labels are already of type '+slug)
            return;
        }

        this.labelsSlug = slug;
        const locations = JSON.parse(this.getData('locations-'+slug));

        await this.removeLabels();

        this.labels = [];
        for(let location of clusterLocations(locations, 250)) { 
            const label = new Label(location, this.renderingCamera, this.earth.globe)
            this.wrapper.add(label.object); 
            this.labels.push(label)
        }
    }

    removeLabels() {
        return new Promise(resolve => {            
            if(!this.labels) {
                resolve();
                return;
            }

            let promises = [];            
            for(let label of this.labels) {
                promises.push(label.remove())
            }

            Promise.all(promises).then(() => {
                for(let label of this.labels) {
                    this.wrapper.remove(label.object);
                }
                delete this.labels;
                resolve();
            })
        })
    }

    setLocationsFromEl(e) { 
        for(let btn of this.$('setLocationsFromEl')) btn.classList.remove('-primary');
        e.currentTarget.classList.add('-primary');
        this.setLabels(JSON.parse(this.getData('locations', e.currentTarget)));
    }

    goToEstimatedUserLocation() {
        const lat = vector3ToLatLong(this.camera.position).lat;        
        const lng = getApproxLongitudeFromUser()           
        this.goToCoordinates({lat,lng}, 0)
    }

    goToCoordinatesFromEl(e) {
        const [lat,lng] = this.getData('coords', e.currentTarget).split(',');
        this.goToCoordinates({lat,lng})
    }

    focusLocation({id, goToCoordinates = true, duration = null }) {
        const label = this.labels.find(i => {
            if(i.data.id) return i.data.id == id;
            else if(i.data.items) {
                if(i.data.items.find(j => j.id == id)) return true;
                else return false;
            } else {
                return false;
            }
        })

        if(!label) return;

        if(goToCoordinates) {
            const {lat,lng} = label.data;
            this.goToCoordinates({lat,lng}, duration)
        }

        if(label) {
            label.setActive(true, id);
        }
    }

    blurLocation() {
        for(const label of this.labels) {
            label.setActive(false)
        }
    }

    resize() {
        super.resize();
    
        this.computeCameraMode?.();
        this.labelRenderer?.setSize?.(this.sizes.width, this.sizes.height);
    }

    onRender() {
        super.onRender();

        this.updateCameras();                

        if(this.labels) {
            for(let label of this.labels) {
                label.update();
            }
        }
        this.labelRenderer.render( this.scene, this.renderingCamera );

        this.earth?.update(this.sunDirection);

        this.updateSun();        
    } 

    destroy() {
        super.destroy()

        this.destroyTweakPane?.();
    }
}
